import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Notes as NotesIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import customerService, { Customer, CreateCustomerData, CustomerNote } from '../services/customerService';
import whatsappTemplateService, { WhatsappTemplate } from '../services/whatsappTemplateService';
import emailTemplateService, { EmailTemplate } from '../services/emailTemplateService';
import emailAccountService, { EmailAccount } from '../services/emailAccountService';
import authService from '../services/authService';


interface CustomerCardProps {
  phoneNumber: string;
  callId: string | null;
  isCallActive: boolean;
  showCustomerCard?: boolean;
  requireFinishCode?: boolean;
  onFinishCodeSelected?: (finishCode: string) => void;
  onFinish?: () => void;
  onClose?: () => void;
  externalData?: any;
  queueData?: {
    customerCardApiUrl?: string;
    customerCardDatasetListUrl?: string;
    customerCardDatasetSchemaUrl?: string;
    customerCardDatasetAddUrl?: string;
    customerCardSelectedDatasetId?: string;
  };
}

const CustomerCard: React.FC<CustomerCardProps> = ({ 
  phoneNumber, 
  callId,
  isCallActive, 
  showCustomerCard, 
  requireFinishCode = false,
  onFinishCodeSelected,
  onFinish, 
  onClose,
  externalData,
  queueData
}) => {
  const currentUser = authService.getCurrentUser();
  const isSupervisorOrAdmin = currentUser?.role === 'supervisor' || currentUser?.role === 'admin';

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateCustomerData>({
    firstName: '',
    lastName: '',
    gsm: '',
    sabitTel: '',
    email: '',
    il: '',
    ilce: '',
    adres: '',
    tc: '',
    notes: '',
  });
  const [newNote, setNewNote] = useState('');
  const [selectedFinishCode, setSelectedFinishCode] = useState('');
  const [callFinishCode, setCallFinishCode] = useState<string>('');
  const [dynamicFields, setDynamicFields] = useState<Array<{key: string, label: string, value: any, type: string}>>([]);
  
  // CRM Dataset workflow states
  const [datasets, setDatasets] = useState<Array<{id: string, name: string}>>([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('');
  const [datasetSchema, setDatasetSchema] = useState<{columns: string[]} | null>(null);
  const [schemaFormData, setSchemaFormData] = useState<Record<string, any>>({});
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [savingToDataset, setSavingToDataset] = useState(false);
  const [editableExternalData, setEditableExternalData] = useState<Record<string, any>>({});

  // WhatsApp States
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [openWhatsappModal, setOpenWhatsappModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Email States
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(false);
  const [savingEmailTemplate, setSavingEmailTemplate] = useState(false);
  const [newEmailTemplateName, setNewEmailTemplateName] = useState('');
  const [newEmailTemplateSubject, setNewEmailTemplateSubject] = useState('');
  const [newEmailTemplateContent, setNewEmailTemplateContent] = useState('');
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState<string | null>(null);




  const finishCodeOptions = [
    'Görüşme Yapıldı',
    'Ses Yok',
    'Kara Liste',
    'Numara Kullanılmıyor',
    'Meşgul',
    'Ulaşılamadı',
    'Daha Sonra Aranacak'
  ];

  // ✨ Telefon numarasını normalize et (hem arama hem de müşteri kaydı için aynı format)
  const normalizePhoneNumber = (number: string): string => {
    if (!number) return '';
    
    // Sadece rakamları al
    let digits = number.replace(/\D/g, '');
    
    // Türkiye numaraları için: 90 ile başlayanları 0 ile başlat
    if (digits.startsWith('90') && digits.length === 12) {
      return '0' + digits.substring(2);
    }
    
    // Zaten 0 ile başlıyorsa olduğu gibi dön
    if (digits.startsWith('0') && digits.length === 11) {
      return digits;
    }
    
    // 10 haneli ise başına 0 ekle
    if (digits.length === 10) {
      return '0' + digits;
    }
    
    return digits;
  };

  // ✨ Modern TextField stil
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#ffffff',
      borderRadius: '8px',
      '& fieldset': { 
        borderColor: '#e0e7ff',
        borderWidth: '2px'
      },
      '&:hover fieldset': { 
        borderColor: '#c7d2fe'
      },
      '&.Mui-focused fieldset': { 
        borderColor: '#6366f1',
        borderWidth: '2px'
      },
    },
    '& .MuiInputLabel-root': { 
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#64748b',
      '&.Mui-focused': {
        color: '#6366f1'
      }
    },
    '& .MuiOutlinedInput-input': { 
      padding: '10px 14px', 
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#1e293b'
    },
    '& .Mui-disabled': { 
      bgcolor: '#f8fafc',
      '& fieldset': {
        borderColor: '#e2e8f0'
      }
    }
  };

  const loadCustomer = useCallback(async () => {
    if (!phoneNumber) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // ✨ Telefon numarasını normalize et (0XXXXXXXXXX formatında)
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      console.log('🔍 Loading customer with normalized phone:', normalizedPhone);
      
      // ✨ Eğer CRM tanımlıysa local DB'den müşteri çekme
      const hasCRMConfig = queueData?.customerCardApiUrl || queueData?.customerCardDatasetListUrl;
      const customerData = hasCRMConfig ? null : await customerService.getCustomerByPhone(normalizedPhone);
      if (customerData) {
        setCustomer(customerData);
        setFormData({
          firstName: customerData.firstName || '',
          lastName: customerData.lastName || '',
          gsm: customerData.gsm || '',
          sabitTel: customerData.sabitTel || '',
          email: customerData.email || '',
          il: customerData.il || '',
          ilce: customerData.ilce || '',
          adres: customerData.adres || '',
          tc: customerData.tc || '',
          notes: '',
        });
      } else {
        // Müşteri bulunamadı, yeni müşteri formu hazırla
        setCustomer(null);
        
        // Harici veriden prefill dene
        const mapExternalToForm = (data: any) => {
          // Eğer data.data varsa (nested), onu kullan
          const actualData = data?.data || data;
          
          return {
            firstName: actualData?.firstName || actualData?.ad || actualData?.ADAY?.split(' ')[0] || '',
            lastName: actualData?.lastName || actualData?.soyad || actualData?.ADAY?.split(' ').slice(1).join(' ') || '',
            gsm: actualData?.gsm || actualData?.phone || actualData?.tel || actualData?.GSM?.replace(/[^0-9]/g, '') || normalizedPhone,
            sabitTel: actualData?.sabitTel || actualData?.phone2 || '',
            email: actualData?.email || '',
            il: actualData?.il || actualData?.city || actualData?.İL || '',
            ilce: actualData?.ilce || actualData?.district || actualData?.İLÇE || '',
            adres: actualData?.adres || actualData?.address || '',
            tc: actualData?.tc || actualData?.identity || '',
            notes: typeof actualData?.notes === 'string' ? actualData?.notes : (actualData?.NOT || '')
          };
        };

        setFormData(externalData ? mapExternalToForm(externalData) : {
          firstName: '',
          lastName: '',
          gsm: normalizedPhone, // Normalize edilmiş numara GSM olarak set edilir
          sabitTel: '',
          email: '',
          il: '',
          ilce: '',
          adres: '',
          tc: '',
          notes: '',
        });
        
        // Standart dışı alanları bul ve dynamicFields'e ekle
        if (externalData) {
          const actualData = externalData?.data || externalData;
          const standardKeys = [
            'firstName', 'lastName', 'ad', 'soyad', 'ADAY',
            'gsm', 'phone', 'tel', 'GSM', 
            'sabitTel', 'phone2', 
            'email', 
            'il', 'city', 'İL', 
            'ilce', 'district', 'İLÇE', 
            'adres', 'address', 
            'tc', 'identity', 
            'notes', 'NOT'
          ];
          const extraFields: Array<{key: string, label: string, value: any, type: string}> = [];
          
          Object.keys(actualData).forEach(key => {
            if (!standardKeys.includes(key) && actualData[key] !== null && actualData[key] !== undefined && actualData[key] !== '') {
              const value = actualData[key];
              const type = typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'checkbox' : 'text';
              
              // Key'i olduğu gibi kullan (Türkçe büyük harfler korunsun)
              const label = key;
              
              extraFields.push({ key, label, value, type });
            }
          });
          
          setDynamicFields(extraFields);
        }
        
        setEditing(true);
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      setError('Müşteri bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, queueData]);

  // Telefon numarası değiştiğinde müşteri bilgilerini getir
  useEffect(() => {
    if (phoneNumber && isCallActive) {
      loadCustomer();
    }
  }, [phoneNumber, isCallActive, loadCustomer]);

  // externalData değiştiğinde editableExternalData'yı güncelle
  useEffect(() => {
    if (externalData && Object.keys(externalData).length > 0) {
      console.log('📋 Raw external data:', externalData);
      
      // Eğer data.data varsa, bunu kullan (nested structure)
      const actualData = externalData.data || externalData;
      
      // Array içindeki ilk elemanı al (eğer array ise)
      const flatData = Array.isArray(actualData) && actualData.length > 0 ? actualData[0] : actualData;
      
      console.log('📋 Setting editable external data:', flatData);
      setEditableExternalData(flatData);
      
      // Eğer externalData içinde datasetId varsa onu kullan
      if (externalData.datasetId) {
        console.log('📊 Using datasetId from external data:', externalData.datasetId);
        setSelectedDatasetId(externalData.datasetId);
      } else if (queueData?.customerCardSelectedDatasetId) {
        // Yoksa queue'dan al
        setSelectedDatasetId(queueData.customerCardSelectedDatasetId);
      }
    }
  }, [externalData, queueData]);

  // CRM Dataset workflow functions
  const fetchDatasetList = useCallback(async () => {
    if (!queueData?.customerCardDatasetListUrl) return;
    
    setLoadingDatasets(true);
    try {
      console.log('📡 Fetching from:', queueData.customerCardDatasetListUrl);
      const response = await fetch(queueData.customerCardDatasetListUrl);
      console.log('📡 Response status:', response.status, response.ok);
      
      if (!response.ok) throw new Error('Dataset list fetch failed');
      
      const data = await response.json();
      console.log('📡 Response data:', data);
      
      // Check if it's directly an array or wrapped in { success: true, data: [...] }
      let datasetArray = [];
      if (Array.isArray(data)) {
        datasetArray = data;
      } else if (data.success && Array.isArray(data.data)) {
        datasetArray = data.data;
      }
      
      if (datasetArray.length > 0) {
        // ✨ Alfabetik sırala (name'e göre)
        datasetArray.sort((a: {id: string, name: string}, b: {id: string, name: string}) => 
          a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
        );
        
        console.log('✅ Setting datasets:', datasetArray.length, 'items (sorted alphabetically)');
        setDatasets(datasetArray);
        
        // If there's a preselected dataset, use it
        if (queueData.customerCardSelectedDatasetId) {
          setSelectedDatasetId(queueData.customerCardSelectedDatasetId);
        }
      } else {
        console.warn('⚠️ Unexpected data format:', data);
      }
    } catch (error) {
      console.error('Error fetching dataset list:', error);
      setError('Dataset listesi yüklenirken hata oluştu');
    } finally {
      setLoadingDatasets(false);
    }
  }, [queueData]);

  const fetchDatasetSchema = useCallback(async (datasetId: string) => {
    if (!queueData?.customerCardDatasetSchemaUrl || !datasetId) return;
    
    setLoadingSchema(true);
    setDatasetSchema(null);
    setSchemaFormData({});
    
    try {
      // Replace :id with actual datasetId
      const url = queueData.customerCardDatasetSchemaUrl.replace(':id', datasetId);
      console.log('📡 Fetching schema from:', url);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Dataset schema fetch failed');
      
      const data = await response.json();
      console.log('📡 Schema response:', data);
      
      // Check if it's directly an object with columns or wrapped
      let schemaData = null;
      if (data.columns && Array.isArray(data.columns)) {
        schemaData = data;
      } else if (data.success && data.data?.columns) {
        schemaData = data.data;
      }
      
      if (schemaData && schemaData.columns) {
        console.log('✅ Setting schema with', schemaData.columns.length, 'columns');
        setDatasetSchema(schemaData);
        
        // Initialize form data with empty values for each column
        const initialFormData: Record<string, any> = {};
        schemaData.columns.forEach((col: string) => {
          initialFormData[col] = '';
        });
        setSchemaFormData(initialFormData);
      } else {
        console.warn('⚠️ Unexpected schema format:', data);
      }
    } catch (error) {
      console.error('Error fetching dataset schema:', error);
      setError('Dataset şeması yüklenirken hata oluştu');
    } finally {
      setLoadingSchema(false);
    }
  }, [queueData]);

  const saveToDataset = useCallback(async () => {
    if (!queueData?.customerCardDatasetAddUrl || !selectedDatasetId) return;
    
    setSavingToDataset(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Replace :id with actual datasetId
      const url = queueData.customerCardDatasetAddUrl.replace(':id', selectedDatasetId);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(schemaFormData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Dataset kayıt ekleme başarısız');
      }
      
      const result = await response.json();
      if (result.success) {
        setSuccess('CRM sistemine kayıt başarıyla eklendi');
        
        // Optionally reload customer data or clear form
        setTimeout(() => {
          loadCustomer();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error saving to dataset:', error);
      setError(error.message || 'CRM sistemine kayıt eklenirken hata oluştu');
    } finally {
      setSavingToDataset(false);
    }
  }, [queueData, selectedDatasetId, schemaFormData, loadCustomer]);

  // WhatsApp Functions
  const loadWhatsappTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await whatsappTemplateService.getTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error loading whatsapp templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (openWhatsappModal) {
      loadWhatsappTemplates();
    }
  }, [openWhatsappModal, loadWhatsappTemplates]);

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateContent.trim()) {
      setError('Lütfen şablon adı ve içeriğini doldurun');
      return;
    }
    setSavingTemplate(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await whatsappTemplateService.createTemplate({
        name: newTemplateName.trim(),
        content: newTemplateContent.trim()
      });
      if (created) {
        setTemplates(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setNewTemplateName('');
        setNewTemplateContent('');
        setSuccess('Şablon başarıyla oluşturuldu');
      } else {
        setError('Şablon oluşturulamadı');
      }
    } catch (err: any) {
      console.error('Error creating template:', err);
      const msg = err?.response?.data?.message || 'Şablon oluşturulurken hata oluştu';
      setError(msg);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    setError(null);
    setSuccess(null);
    try {
      const success = await whatsappTemplateService.deleteTemplate(id);
      if (success) {
        setTemplates(prev => prev.filter(t => t._id !== id));
        setSuccess('Şablon başarıyla silindi');
      } else {
        setError('Şablon silinemedi');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Şablon silinirken hata oluştu');
    }
  };

  const handleSendWhatsapp = (templateContent: string) => {
    const targetNumber = formData.gsm || phoneNumber;
    if (!targetNumber) {
      setError('Gönderilecek telefon numarası bulunamadı');
      return;
    }

    let resolvedText = templateContent
      .replace(/{ad}/g, formData.firstName || '')
      .replace(/{soyad}/g, formData.lastName || '')
      .replace(/{telefon}/g, targetNumber);

    let digits = targetNumber.replace(/\D/g, '');
    let normalizedPhone = '';

    if (digits.startsWith('90') && digits.length === 12) {
      normalizedPhone = digits;
    } else if (digits.startsWith('0') && digits.length === 11) {
      normalizedPhone = '90' + digits.substring(1);
    } else if (digits.length === 10) {
      normalizedPhone = '90' + digits;
    } else {
      normalizedPhone = digits;
    }

    if (!normalizedPhone) {
      setError('Geçersiz telefon numarası');
      return;
    }

    const url = `https://web.whatsapp.com/send?phone=${normalizedPhone}&text=${encodeURIComponent(resolvedText)}`;
    window.open(url, '_blank');
    setOpenWhatsappModal(false);
  };

  const [newEmailTemplateAccountId, setNewEmailTemplateAccountId] = useState<string>('');

  const handleSendEmail = async () => {
    const targetEmail = formData.email;
    if (!targetEmail) {
      setError('Gönderilecek e-posta adresi bulunamadı');
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      setError('Konu ve mesaj alanları boş bırakılamaz');
      return;
    }

    setSendingEmail(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await customerService.sendEmail(
        targetEmail.trim(),
        emailSubject.trim(),
        emailBody.trim(),
        selectedEmailAccountId
      );

      if (result.success) {
        setSuccess('E-posta başarıyla gönderildi');
        setOpenEmailModal(false);
        setEmailSubject('');
        setEmailBody('');
        setSelectedEmailAccountId(null);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Error sending email:', err);
      setError(err?.message || 'E-posta gönderilirken beklenmeyen bir hata oluştu');
    } finally {
      setSendingEmail(false);
    }
  };

  // Load email templates
  const loadEmailTemplates = useCallback(async () => {
    setLoadingEmailTemplates(true);
    try {
      const data = await emailTemplateService.getTemplates();
      setEmailTemplates(data);
    } catch (err) {
      console.error('Error loading email templates:', err);
    } finally {
      setLoadingEmailTemplates(false);
    }
  }, []);

  // Load email accounts
  const loadEmailAccounts = useCallback(async () => {
    try {
      const data = await emailAccountService.getAccounts();
      setEmailAccounts(data);
    } catch (err) {
      console.error('Error loading email accounts:', err);
    }
  }, []);

  useEffect(() => {
    if (openEmailModal) {
      loadEmailTemplates();
      loadEmailAccounts();
    }
  }, [openEmailModal, loadEmailTemplates, loadEmailAccounts]);

  const handleCreateEmailTemplate = async () => {
    if (!newEmailTemplateName.trim() || !newEmailTemplateSubject.trim() || !newEmailTemplateContent.trim()) {
      setError('Lütfen şablon adı, konu ve içeriğini doldurun');
      return;
    }
    setSavingEmailTemplate(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await emailTemplateService.createTemplate({
        name: newEmailTemplateName.trim(),
        subject: newEmailTemplateSubject.trim(),
        content: newEmailTemplateContent.trim(),
        emailAccountId: newEmailTemplateAccountId || null
      });
      if (created) {
        setEmailTemplates(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
        setNewEmailTemplateName('');
        setNewEmailTemplateSubject('');
        setNewEmailTemplateContent('');
        setNewEmailTemplateAccountId('');
        setSuccess('Şablon başarıyla oluşturuldu');
      } else {
        setError('Şablon oluşturulamadı');
      }
    } catch (err: any) {
      console.error('Error creating email template:', err);
      const msg = err?.response?.data?.message || 'Şablon oluşturulurken hata oluştu';
      setError(msg);
    } finally {
      setSavingEmailTemplate(false);
    }
  };

  const handleDeleteEmailTemplate = async (id: string) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    setError(null);
    setSuccess(null);
    try {
      const success = await emailTemplateService.deleteTemplate(id);
      if (success) {
        setEmailTemplates(prev => prev.filter(t => t._id !== id));
        setSuccess('Şablon başarıyla silindi');
      } else {
        setError('Şablon silinemedi');
      }
    } catch (err) {
      console.error('Error deleting email template:', err);
      setError('Şablon silinirken hata oluştu');
    }
  };

  const handleApplyEmailTemplate = (template: EmailTemplate) => {
    const targetNumber = formData.gsm || phoneNumber || '';
    
    // Değişkenleri yerlerine koy
    let resolvedSubject = template.subject
      .replace(/{ad}/g, formData.firstName || '')
      .replace(/{soyad}/g, formData.lastName || '')
      .replace(/{telefon}/g, targetNumber);

    let resolvedContent = template.content
      .replace(/{ad}/g, formData.firstName || '')
      .replace(/{soyad}/g, formData.lastName || '')
      .replace(/{telefon}/g, targetNumber);

    setEmailSubject(resolvedSubject);
    setEmailBody(resolvedContent);
    setSelectedEmailAccountId(template.emailAccountId || null);
    setSuccess('Şablon başarıyla uygulandı');
  };




  // Load datasets when component mounts if URLs are configured
  useEffect(() => {
    // Check if externalData is empty object
    const hasExternalData = externalData && Object.keys(externalData).length > 0;
    
    console.log('🔍 Dataset fetch check:', {
      hasDatasetUrl: !!queueData?.customerCardDatasetListUrl,
      customer: !!customer,
      hasExternalData,
      datasetsLength: datasets.length
    });
    
    if (queueData?.customerCardDatasetListUrl && !customer && !hasExternalData) {
      console.log('✅ Fetching dataset list...');
      fetchDatasetList();
    }
  }, [queueData, customer, externalData, fetchDatasetList]);

  // Load schema when dataset is selected
  useEffect(() => {
    if (selectedDatasetId) {
      fetchDatasetSchema(selectedDatasetId);
    }
  }, [selectedDatasetId, fetchDatasetSchema]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let savedCustomer: Customer | null = null;
      
      if (customer) {
        // Mevcut müşteriyi güncelle
        savedCustomer = await customerService.updateCustomer(customer._id!, {
          ...formData,
          notes: newNote || undefined
        });
      } else {
        // Yeni müşteri oluştur
        savedCustomer = await customerService.createCustomer({
          ...formData,
          notes: newNote || undefined
        });
      }

      if (savedCustomer) {
        setCustomer(savedCustomer);
        setEditing(false);
        setNewNote('');
        setSuccess(customer ? 'Müşteri bilgileri güncellendi' : 'Yeni müşteri oluşturuldu');
      } else {
        setError('Müşteri kaydedilemedi');
      }
    } catch (error: any) {
      console.error('❌ Error creating customer:', error);
      // Backend'den gelen hata mesajını göster
      const errorMessage = error?.response?.data?.message || 'Müşteri kaydedilirken hata oluştu';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (customer) {
      // Mevcut müşteri bilgilerini geri yükle
      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        gsm: customer.gsm || '',
        sabitTel: customer.sabitTel || '',
        email: customer.email || '',
        il: customer.il || '',
        ilce: customer.ilce || '',
        adres: customer.adres || '',
        tc: customer.tc || '',
        notes: '',
      });
    } else {
      // Yeni müşteri formunu temizle
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      setFormData({
        firstName: '',
        lastName: '',
        gsm: normalizedPhone,
        sabitTel: '',
        email: '',
        il: '',
        ilce: '',
        adres: '',
        tc: '',
        notes: '',
      });
    }
    setNewNote('');
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (field: keyof CreateCustomerData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Finish code kaydet (hem müşteriye hem call'a)
  const handleSaveFinishCode = async () => {
    if (!selectedFinishCode) {
      setError('Lütfen bir sonuç kodu seçin');
      return;
    }

    if (!customer || !customer._id) {
      setError('Müşteri bilgisi bulunamadı');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // 1. Müşteriye finish code kaydet
      const updatedCustomer = await customerService.addFinishCode(
        customer._id,
        selectedFinishCode
      );

      if (updatedCustomer) {
        setCustomer(updatedCustomer);
        setSuccess('Sonuç kodu kaydedildi');
      }

      // 2. Eğer requireFinishCode true ise ve callback varsa, call'ı da sonlandır
      if (requireFinishCode && onFinishCodeSelected && callId) {
        await onFinishCodeSelected(selectedFinishCode);
      }
      
      setSelectedFinishCode('');
    } catch (error: any) {
      console.error('❌ Error saving finish code:', error);
      const errorMessage = error?.response?.data?.message || 'Sonuç kodu kaydedilirken hata oluştu';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Call finish code seçimi (çağrı bittikten sonra zorunlu)
  const handleCallFinishCode = async () => {
    console.log('🔍 handleCallFinishCode called:', { callFinishCode, callId, hasCallback: !!onFinishCodeSelected });
    
    if (!callFinishCode) {
      setError('Lütfen bir sonuç kodu seçin');
      return;
    }

    if (!onFinishCodeSelected) {
      console.error('❌ onFinishCodeSelected callback is missing!');
      setError('Callback fonksiyonu bulunamadı');
      return;
    }

    if (!callId) {
      console.error('❌ callId is missing!');
      setError('Çağrı ID bulunamadı');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      console.log('📞 Calling onFinishCodeSelected with:', callFinishCode);
      // Call'ı finish code ile sonlandır
      await onFinishCodeSelected(callFinishCode);
      
      // Eğer müşteri varsa ona da kaydet
      if (customer && customer._id) {
        console.log('👤 Saving finish code to customer:', customer._id);
        await customerService.addFinishCode(customer._id, callFinishCode);
      }
      
      setCallFinishCode('');
      setSuccess('Çağrı sonlandırıldı');
      console.log('✅ Call finish code saved successfully');
    } catch (error: any) {
      console.error('❌ Error saving call finish code:', error);
      const errorMessage = error?.response?.data?.message || 'Finish code kaydedilemedi';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // ✨ Müşteri kartı görünür olmalı: ya arama aktifse ya da showCustomerCard true ise
  if ((!isCallActive && !showCustomerCard) || !phoneNumber) {
    return null;
  }

  return (
    <Card 
      sx={{ 
        mb: 0,
        width: '100%',
        height: '100%',
        bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: 'none',
        overflow: 'auto',
        background: 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 100%)',
      }}
    >
      <CardContent sx={{ p: 4, '&:last-child': { pb: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between" 
          mb={3}
          pb={2}
          borderBottom="2px solid #e0e7ff"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography 
                variant="h5" 
                component="h2"
                sx={{ 
                  color: '#1e293b',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  letterSpacing: '-0.02em'
                }}
              >
                Müşteri Bilgileri
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              >
                {customer ? 'Kayıtlı Müşteri' : 'Yeni Müşteri'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {customer && !editing && (
              <IconButton 
                onClick={() => setEditing(true)} 
                size="small"
                sx={{
                  bgcolor: '#f1f5f9',
                  width: 40,
                  height: 40,
                  '&:hover': { 
                    bgcolor: '#e0e7ff',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <EditIcon fontSize="medium" sx={{ color: '#6366f1' }} />
              </IconButton>
            )}
            {editing && (
              <Button
                onClick={handleSave}
                variant="contained"
                size="medium"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{ 
                  textTransform: 'none',
                  bgcolor: '#6366f1',
                  borderRadius: '10px',
                  px: 3,
                  py: 1,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#4f46e5'
                  },
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            )}
            {!isCallActive && onClose && !requireFinishCode && (
              <IconButton 
                onClick={onClose} 
                size="small"
                sx={{
                  bgcolor: '#fee2e2',
                  width: 40,
                  height: 40,
                  '&:hover': { 
                    bgcolor: '#fecaca',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
                title="Kapat"
              >
                <CloseIcon fontSize="medium" sx={{ color: '#dc2626' }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={32} sx={{ color: '#6366f1' }} />
            <Typography variant="body2" sx={{ ml: 2, color: '#64748b', fontWeight: 500 }}>
              Müşteri bilgileri yükleniyor...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2, 
              borderRadius: 2,
              bgcolor: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              '& .MuiAlert-icon': {
                color: '#dc2626'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2, 
              borderRadius: 2,
              bgcolor: '#d1fae5',
              color: '#065f46',
              border: '1px solid #a7f3d0',
              '& .MuiAlert-icon': {
                color: '#059669'
              }
            }}
          >
            {success}
          </Alert>
        )}

        {/* Zorunlu Finish Code Seçimi - Çağrı bittikten sonra */}
        {requireFinishCode && (
          <Box 
            sx={{ 
              mb: 3,
              p: 2.5,
              bgcolor: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 1.5, 
                color: '#475569',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.9rem'
              }}
            >
              <CheckCircleIcon sx={{ mr: 1, fontSize: 18, color: '#6366f1' }} />
              Sonuç Kodu (Zorunlu)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                select
                sx={{
                  ...textFieldStyle,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white'
                  }
                }}
                size="small"
                fullWidth
                value={callFinishCode}
                onChange={(e) => setCallFinishCode(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Seçiniz...</option>
                {finishCodeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={handleCallFinishCode}
                disabled={!callFinishCode || saving}
                sx={{
                  bgcolor: '#152b69',
                  '&:hover': { bgcolor: '#0f1f4d' },
                  minWidth: '140px',
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Kaydet ve Kapat'}
              </Button>
            </Box>
          </Box>
        )}

        {/* ✨ CRM Dataset Form - Show ONLY this if CRM is configured */}
        {(() => {
          const hasCRMConfig = queueData?.customerCardDatasetListUrl;
          return hasCRMConfig;
        })() ? (
          // CRM Mode - Show dataset form OR external data
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* If we have external data, show it */}
            {editableExternalData && Object.keys(editableExternalData).length > 0 ? (
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 2, color: '#475569', fontWeight: 600, fontSize: '0.95rem' }}>
                  📋 CRM Verisi:
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                    '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
                    '@media (max-width: 600px)': { gridTemplateColumns: '1fr' }
                  }}
                >
                  {Object.entries(editableExternalData).map(([key, value]) => (
                    <TextField
                      key={key}
                      label={key}
                      size="small"
                      value={typeof value === 'object' ? JSON.stringify(value) : String(value || '')}
                      onChange={(e) => {
                        setEditableExternalData({ ...editableExternalData, [key]: e.target.value });
                      }}
                      sx={textFieldStyle}
                    />
                  ))}
                </Box>
                
                {/* Update Button for External Data */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!queueData?.customerCardDatasetAddUrl) {
                        setError('Dataset add URL tanımlı değil');
                        return;
                      }
                      
                      setSavingToDataset(true);
                      setError(null);
                      setSuccess(null);
                      
                      try {
                        // Use datasetId from external data, or preselected, or empty
                        const datasetId = selectedDatasetId || queueData.customerCardSelectedDatasetId || '';
                        const url = queueData.customerCardDatasetAddUrl.replace(':id', datasetId);
                        console.log('📤 Sending to CRM with datasetId:', datasetId);
                        console.log('📤 URL:', url);
                        console.log('📤 Data:', editableExternalData);
                        
                        const response = await fetch(url, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(editableExternalData)
                        });
                        
                        console.log('📡 CRM response status:', response.status, response.ok);
                        
                        if (!response.ok) {
                          const errorText = await response.text();
                          console.error('❌ CRM error:', errorText);
                          throw new Error('Güncelleme başarısız');
                        }
                        
                        const result = await response.json();
                        console.log('✅ CRM response:', result);
                        
                        setSuccess('CRM sistemine kayıt güncellendi');
                      } catch (error: any) {
                        console.error('❌ Error updating to CRM:', error);
                        setError(error.message || 'CRM güncelleme hatası');
                      } finally {
                        setSavingToDataset(false);
                      }
                    }}
                    disabled={savingToDataset}
                    startIcon={savingToDataset ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SaveIcon />}
                    sx={{
                      bgcolor: '#6366f1',
                      '&:hover': { bgcolor: '#4f46e5' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.2,
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                    }}
                  >
                    {savingToDataset ? 'Kaydediliyor...' : 'CRM Sistemine Kaydet'}
                  </Button>
                </Box>
              </Box>
            ) : (
              // No data yet, show dataset selector
              <>
            {/* Dataset Selector */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1.5, color: '#475569', fontWeight: 600, fontSize: '0.95rem' }}>
                📊 Dataset Seçin:
              </Typography>
              <select
                value={selectedDatasetId}
                onChange={(e) => setSelectedDatasetId(e.target.value)}
                disabled={loadingDatasets}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '2px solid #e0e7ff',
                  backgroundColor: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <option value="">-- Dataset Seçin --</option>
                {datasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name}
                  </option>
                ))}
              </select>
            </Box>

            {/* Schema Loading */}
            {loadingSchema && (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <CircularProgress size={40} sx={{ color: '#6366f1' }} />
                <Typography variant="body2" sx={{ mt: 2, color: '#64748b', fontWeight: 500 }}>
                  Şema yükleniyor...
                </Typography>
              </Box>
            )}

            {/* Schema Form */}
            {datasetSchema && !loadingSchema && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ mb: 2, color: '#475569', fontWeight: 600, fontSize: '0.95rem' }}>
                  📝 Form Alanları ({datasetSchema.columns.length}):
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                    flex: 1,
                    '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
                    '@media (max-width: 600px)': { gridTemplateColumns: '1fr' }
                  }}
                >
                  {datasetSchema.columns.map((col) => (
                    <TextField
                      key={col}
                      label={col}
                      size="small"
                      value={schemaFormData[col] || ''}
                      onChange={(e) =>
                        setSchemaFormData({ ...schemaFormData, [col]: e.target.value })
                      }
                      sx={textFieldStyle}
                    />
                  ))}
                </Box>

                {/* Save Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={saveToDataset}
                    disabled={savingToDataset}
                    startIcon={savingToDataset ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SaveIcon />}
                    sx={{
                      bgcolor: '#6366f1',
                      '&:hover': { bgcolor: '#4f46e5' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.2,
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                    }}
                  >
                    {savingToDataset ? 'Kaydediliyor...' : 'CRM Sistemine Kaydet'}
                  </Button>
                </Box>
              </Box>
            )}
            </>
            )}
          </Box>
        ) : (
          // Standard Mode - Show classic customer card
          <>
        {!loading && (
          <Box sx={{ 
            display: 'flex',
            gap: 3,
            flex: 1,
          }}>
            {/* Sol Taraf - Form Alanları */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: 2,
              flex: 1,
              '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
              '@media (max-width: 600px)': { gridTemplateColumns: '1fr' }
            }}>
            <TextField
              sx={textFieldStyle}
              label="Ad"
              size="small"
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              disabled={!editing}
            />
            <TextField
              sx={textFieldStyle}
              label="Soyad"
              size="small"
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              disabled={!editing}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                sx={{ ...textFieldStyle, flex: 1 }}
                label="GSM"
                size="small"
                value={formData.gsm}
                onChange={handleInputChange('gsm')}
                disabled={!editing}
              />
              {(formData.gsm || phoneNumber) && (
                <IconButton
                  onClick={() => setOpenWhatsappModal(true)}
                  sx={{
                    bgcolor: '#e8f5e9',
                    color: '#25D366',
                    border: '2px solid #a5d6a7',
                    borderRadius: '10px',
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: '#c8e6c9',
                      color: '#128C7E',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s'
                  }}
                  title="WhatsApp Mesaj Şablonu Gönder"
                >
                  <WhatsAppIcon />
                </IconButton>
              )}
            </Box>
            <TextField
              sx={textFieldStyle}
              label="Sabit Tel"
              size="small"
              value={formData.sabitTel}
              onChange={handleInputChange('sabitTel')}
              disabled={!editing}
            />
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                sx={{ ...textFieldStyle, flexGrow: 1 }}
                label="E-posta"
                size="small"
                value={formData.email}
                onChange={handleInputChange('email')}
                disabled={!editing}
              />
              {formData.email && (
                <IconButton
                  onClick={() => setOpenEmailModal(true)}
                  sx={{
                    bgcolor: '#e0f2fe',
                    color: '#0284c7',
                    border: '2px solid #bae6fd',
                    borderRadius: '10px',
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: '#bae6fd',
                      color: '#0369a1',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s'
                  }}
                  title="E-posta Gönder"
                >
                  <EmailIcon />
                </IconButton>
              )}
            </Box>

            <TextField
              sx={textFieldStyle}
              label="İl"
              size="small"
              value={formData.il}
              onChange={handleInputChange('il')}
              disabled={!editing}
            />
            <TextField
              sx={textFieldStyle}
              label="İlçe"
              size="small"
              value={formData.ilce}
              onChange={handleInputChange('ilce')}
              disabled={!editing}
            />
            <TextField
              sx={textFieldStyle}
              label="T.C. Kimlik No"
              size="small"
              value={formData.tc}
              onChange={handleInputChange('tc')}
              disabled={!editing}
            />
            <TextField
              sx={textFieldStyle}
              label="Adres"
              size="small"
              value={formData.adres}
              onChange={handleInputChange('adres')}
              disabled={!editing}
              multiline
              rows={2}
              InputProps={{
                sx: { bgcolor: editing ? '#ffffff' : '#f8fafc' }
              }}
            />
            
            {/* Yeni Not Ekleme - Sadece editing modunda */}
            {editing && (
              <TextField
                sx={textFieldStyle}
                label="Yeni Not"
                size="small"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                multiline
                rows={2}
                placeholder="Bu arama ile ilgili notunuzu yazın..."
                InputProps={{
                  sx: { bgcolor: '#fef3c7' }
                }}
              />
            )}
            
            {/* Dinamik Alanlar - Harici API'den gelen ekstra veriler */}
            {dynamicFields.length > 0 && (
              <>
                <Box sx={{ gridColumn: '1 / -1', mt: 2, mb: 1 }}>
                  <Divider sx={{ borderColor: '#cbd5e1', borderWidth: 1 }}>
                    <Chip 
                      label={`Dış Sistemden Gelen Ek Bilgiler (${dynamicFields.length})`}
                      size="small"
                      sx={{ 
                        bgcolor: '#e0f2fe', 
                        color: '#075985',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        px: 1
                      }}
                    />
                  </Divider>
                </Box>
                {dynamicFields.map((field) => (
                  <TextField
                    key={field.key}
                    sx={{
                      ...textFieldStyle,
                      '& .MuiOutlinedInput-root': {
                        ...textFieldStyle['& .MuiOutlinedInput-root'],
                        bgcolor: '#f0f9ff',
                        borderColor: '#bae6fd'
                      },
                      '& .MuiInputLabel-root': {
                        color: '#0c4a6e',
                        fontWeight: 700,
                        fontSize: '0.9rem'
                      }
                    }}
                    label={field.label}
                    size="small"
                    value={field.value}
                    disabled
                    type={field.type}
                    multiline={field.type === 'text' && String(field.value).length > 50}
                    rows={field.type === 'text' && String(field.value).length > 50 ? 2 : 1}
                    InputProps={{
                      sx: { 
                        bgcolor: '#f0f9ff',
                        '& input, & textarea': {
                          color: '#0c4a6e',
                          fontWeight: 600,
                          fontSize: '0.9rem'
                        }
                      },
                      startAdornment: (
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: '#0ea5e9', 
                          mr: 1,
                          flexShrink: 0
                        }} />
                      )
                    }}
                    helperText="Dış CRM Sistemi"
                    FormHelperTextProps={{
                      sx: {
                        color: '#64748b',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        fontStyle: 'italic'
                      }
                    }}
                  />
                ))}
              </>
            )}
          </Box>

          {/* Sağ Taraf - Geçmiş Finish Codes ve Notlar */}
          {customer && (customer.finishCodes?.length || customer.notes?.length) ? (
            <Box
              sx={{
                width: '350px',
                minWidth: '350px',
                '@media (max-width: 1200px)': { display: 'none' },
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              {/* Geçmiş Finish Codes */}
              {customer.finishCodes && customer.finishCodes.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.95rem'
                    }}
                  >
                    <CheckCircleIcon sx={{ mr: 1, fontSize: 20, color: '#152b69' }} />
                    Geçmiş Sonuç Kodları ({customer.finishCodes.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: '200px',
                      overflow: 'auto',
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                      p: 2,
                      border: '1px solid #e0e7ff',
                      '&::-webkit-scrollbar': {
                        width: '6px'
                      },
                      '&::-webkit-scrollbar-track': {
                        bgcolor: '#f1f5f9',
                        borderRadius: '10px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#cbd5e1',
                        borderRadius: '10px',
                        '&:hover': {
                          bgcolor: '#94a3b8'
                        }
                      }
                    }}
                  >
                    {customer.finishCodes
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((finishCode, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: index < customer.finishCodes!.length - 1 ? '1px solid #e0e7ff' : 'none',
                          '&:last-child': {
                            mb: 0,
                            pb: 0
                          }
                        }}
                      >
                        <Chip
                          label={finishCode.code}
                          size="small"
                          sx={{
                            bgcolor: '#152b69',
                            color: 'white',
                            fontWeight: 600,
                            mb: 0.5
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#64748b',
                            fontSize: '0.7rem',
                            mt: 0.5
                          }}
                        >
                          {new Date(finishCode.createdAt).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Geçmiş Notlar */}
              {customer.notes && customer.notes.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.95rem'
                    }}
                  >
                    <NotesIcon sx={{ mr: 1, fontSize: 20, color: '#6366f1' }} />
                    Geçmiş Notlar ({customer.notes.length})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: '200px',
                      overflow: 'auto',
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                      p: 2,
                      border: '1px solid #e0e7ff',
                      '&::-webkit-scrollbar': {
                        width: '6px'
                      },
                      '&::-webkit-scrollbar-track': {
                        bgcolor: '#f1f5f9',
                        borderRadius: '10px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#cbd5e1',
                        borderRadius: '10px',
                        '&:hover': {
                          bgcolor: '#94a3b8'
                        }
                      }
                    }}
                  >
                {customer.notes
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((note, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 1.5, 
                      pb: 1.5, 
                      borderBottom: index < customer.notes!.length - 1 ? '1px solid #e0e7ff' : 'none',
                      '&:last-child': {
                        mb: 0,
                        pb: 0
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        display: 'block', 
                        fontSize: '0.875rem',
                        color: '#1e293b',
                        mb: 0.5,
                        lineHeight: 1.6,
                        fontWeight: 500
                      }}
                    >
                      {note.content}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#64748b', 
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <span style={{ fontSize: '10px' }}>●</span>
                      {new Date(note.createdAt).toLocaleString('tr-TR')}
                    </Typography>
                  </Box>
                ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
        )
}
        {/* Mobile view - Notlar altta gorunur (1200px altinda) */}
        {!loading && customer && customer.notes && customer.notes.length > 0 && (
          <Box 
            mt={3} 
            pt={3} 
            borderTop="2px solid #e0e7ff"
            sx={{
              '@media (min-width: 1200px)': { display: 'none' }
            }}
          >
            <Typography 
              variant="subtitle2" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5, 
                color: '#475569', 
                fontWeight: 700,
                fontSize: '0.95rem'
              }}
            >
              <NotesIcon sx={{ mr: 1, fontSize: 20, color: '#6366f1' }} />
              Geçmiş Notlar ({customer.notes.length})
            </Typography>
            <Box 
              sx={{ 
                maxHeight: 180, 
                overflow: 'auto', 
                bgcolor: '#f8fafc', 
                borderRadius: 2, 
                p: 2,
                border: '1px solid #e0e7ff',
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: '#f1f5f9',
                  borderRadius: '10px'
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#cbd5e1',
                  borderRadius: '10px',
                  '&:hover': {
                    bgcolor: '#94a3b8'
                  }
                }
              }}
            >
              {customer.notes
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((note, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: 1.5, 
                    pb: 1.5, 
                    borderBottom: index < customer.notes!.length - 1 ? '1px solid #e0e7ff' : 'none',
                    '&:last-child': {
                      mb: 0,
                      pb: 0
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: 'block', 
                      fontSize: '0.875rem',
                      color: '#1e293b',
                      mb: 0.5,
                      lineHeight: 1.6,
                      fontWeight: 500
                    }}
                  >
                    {note.content}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#64748b', 
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <span style={{ fontSize: '10px' }}>●</span>
                    {new Date(note.createdAt).toLocaleString('tr-TR')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Butonlar - Modern */}
        {editing && !loading && (
          <Box 
            display="flex" 
            justifyContent="flex-end" 
            gap={1.5} 
            mt={3}
            pt={3}
            borderTop="2px solid #e0e7ff"
          >
            <Button
              onClick={handleCancel}
              size="medium"
              startIcon={<CancelIcon />}
              disabled={saving}
              sx={{ 
                textTransform: 'none',
                color: '#64748b',
                borderRadius: '8px',
                px: 2.5,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f1f5f9'
                }
              }}
            >
              İptal
            </Button>
            <Button
              onClick={() => onFinish?.()}
              size="medium"
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              sx={{ 
                textTransform: 'none',
                borderColor: '#10b981',
                color: '#10b981',
                borderRadius: '8px',
                px: 2.5,
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#059669',
                  bgcolor: '#d1fae5',
                  borderWidth: '2px'
                }
              }}
            >
              Çözüldü
            </Button>
          </Box>
        )}
        </>
        )}
      </CardContent>

      {/* WhatsApp Modal */}
      <Dialog
        open={openWhatsappModal}
        onClose={() => setOpenWhatsappModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            background: 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 100%)'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700} color="#1e293b">
              WhatsApp Şablon Mesaj Gönder
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Target number notice */}
          <Box display="flex" gap={1} bgcolor="#e8f5e9" p={1.5} borderRadius={2} border="1px solid #c8e6c9" alignItems="center">
            <Typography variant="body2" color="#2e7d32" fontWeight={600}>
              Alıcı Numarası: {formData.gsm || phoneNumber}
            </Typography>
          </Box>

          {/* New Template Form */}
          {isSupervisorOrAdmin && (
            <Box component="fieldset" sx={{ border: '1px solid #cbd5e1', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <legend style={{ padding: '0 8px', color: '#475569', fontSize: '0.85rem', fontWeight: 700 }}>
                Yeni Şablon Ekle
              </legend>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  label="Şablon Adı"
                  size="small"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  sx={{ ...textFieldStyle, width: '250px' }}
                  placeholder="Örn: Karşılama"
                />
                <Button
                  variant="contained"
                  onClick={handleCreateTemplate}
                  disabled={savingTemplate || !newTemplateName.trim() || !newTemplateContent.trim()}
                  startIcon={savingTemplate ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  sx={{
                    bgcolor: '#6366f1',
                    '&:hover': { bgcolor: '#4f46e5' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2
                  }}
                >
                  Şablonu Kaydet
                </Button>
              </Box>
              <TextField
                label="Şablon İçeriği"
                size="small"
                multiline
                rows={3}
                fullWidth
                value={newTemplateContent}
                onChange={(e) => setNewTemplateContent(e.target.value)}
                placeholder="Mesaj içeriğini yazın. Dinamik alanlar: {ad}, {soyad}, {telefon}"
                sx={textFieldStyle}
              />
            </Box>
          )}

          {/* Templates list */}
          <Box>
            <Typography variant="subtitle2" color="#475569" fontWeight={700} mb={1.5}>
              Kayıtlı Genel Şablonlar
            </Typography>
            {loadingTemplates ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={30} sx={{ color: '#6366f1' }} />
              </Box>
            ) : templates.length === 0 ? (
              <Typography variant="body2" color="#64748b" align="center" py={4} sx={{ fontStyle: 'italic' }}>
                {isSupervisorOrAdmin 
                  ? "Kayıtlı şablon bulunamadı. Yukarıdaki formdan yeni bir şablon oluşturabilirsiniz."
                  : "Kayıtlı şablon bulunamadı."}
              </Typography>
            ) : (
              <Box display="flex" flexDirection="column" gap={2} sx={{ maxHeight: '350px', overflowY: 'auto', pr: 1 }}>
                {templates.map((tmpl) => (
                  <Card key={tmpl._id} sx={{ p: 2, border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: 2, '&:hover': { borderColor: '#cbd5e1' } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="subtitle2" color="#1e293b" fontWeight={700}>
                        {tmpl.name}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleSendWhatsapp(tmpl.content)}
                          startIcon={<WhatsAppIcon />}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                        >
                          Gönder
                        </Button>
                        {isSupervisorOrAdmin && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => tmpl._id && handleDeleteTemplate(tmpl._id)}
                            sx={{ bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' }, borderRadius: 1.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="#475569"
                      sx={{
                        whiteSpace: 'pre-line',
                        bgcolor: '#f8fafc',
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px dashed #cbd5e1',
                        fontSize: '0.85rem'
                      }}
                    >
                      {tmpl.content}
                    </Typography>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setOpenWhatsappModal(false)}
            variant="outlined"
            sx={{ textTransform: 'none', color: '#64748b', borderColor: '#cbd5e1', borderRadius: 2, fontWeight: 600 }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* E-posta Modal */}
      <Dialog
        open={openEmailModal}
        onClose={() => !sendingEmail && setOpenEmailModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            background: 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 100%)'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon sx={{ color: '#0284c7', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700} color="#1e293b">
              Müşteriye E-posta Gönder
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Sol Panel: Şablonlar */}
          <Box sx={{ width: { xs: '100%', md: '40%' }, borderRight: { xs: 'none', md: '1px solid #e2e8f0' }, pr: { xs: 0, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="#475569" fontWeight={700}>
              E-posta Şablonları
            </Typography>

            {/* Yeni Şablon Ekleme Formu */}
            {isSupervisorOrAdmin && (
              <Box component="fieldset" sx={{ border: '1px solid #cbd5e1', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <legend style={{ padding: '0 8px', color: '#475569', fontSize: '0.85rem', fontWeight: 700 }}>
                  Yeni Şablon Ekle
                </legend>
                <TextField
                  label="Şablon Adı"
                  size="small"
                  fullWidth
                  value={newEmailTemplateName}
                  onChange={(e) => setNewEmailTemplateName(e.target.value)}
                  sx={textFieldStyle}
                  placeholder="Örn: Teklif Şablonu"
                />
                <TextField
                  label="Şablon Konusu"
                  size="small"
                  fullWidth
                  value={newEmailTemplateSubject}
                  onChange={(e) => setNewEmailTemplateSubject(e.target.value)}
                  sx={textFieldStyle}
                  placeholder="E-posta konusu..."
                />
                <TextField
                  label="Şablon İçeriği"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  value={newEmailTemplateContent}
                  onChange={(e) => setNewEmailTemplateContent(e.target.value)}
                  placeholder="Mesaj içeriği... Dinamik alanlar: {ad}, {soyad}, {telefon}"
                  sx={textFieldStyle}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateEmailTemplate}
                  disabled={savingEmailTemplate || !newEmailTemplateName.trim() || !newEmailTemplateSubject.trim() || !newEmailTemplateContent.trim()}
                  startIcon={savingEmailTemplate ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  sx={{
                    bgcolor: '#0284c7',
                    '&:hover': { bgcolor: '#0369a1' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    mt: 1
                  }}
                >
                  Şablonu Kaydet
                </Button>
              </Box>
            )}

            {/* Kayıtlı Şablonlar Listesi */}
            <Box sx={{ flexGrow: 1, maxHeight: '350px', overflowY: 'auto', pr: 0.5 }}>
              {loadingEmailTemplates ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={30} sx={{ color: '#0284c7' }} />
                </Box>
              ) : emailTemplates.length === 0 ? (
                <Typography variant="body2" color="#64748b" align="center" py={4} sx={{ fontStyle: 'italic' }}>
                  {isSupervisorOrAdmin
                    ? "Kayıtlı şablon bulunamadı. Yukarıdaki formdan yeni bir şablon oluşturabilirsiniz."
                    : "Kayıtlı şablon bulunamadı."}
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {emailTemplates.map((tmpl) => (
                    <Card
                      key={tmpl._id}
                      sx={{
                        p: 1.5,
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none',
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#0284c7', bgcolor: '#f0f9ff' },
                        transition: 'all 0.2s'
                      }}
                      onClick={() => handleApplyEmailTemplate(tmpl)}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="subtitle2" color="#1e293b" fontWeight={700}>
                          {tmpl.name}
                        </Typography>
                        {isSupervisorOrAdmin && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Card'a tıklama event'ini engelle
                              tmpl._id && handleDeleteEmailTemplate(tmpl._id);
                            }}
                            sx={{
                              bgcolor: '#fee2e2',
                              '&:hover': { bgcolor: '#fecaca' },
                              borderRadius: 1.5,
                              p: 0.5
                            }}
                          >
                            <DeleteIcon fontSize="small" style={{ fontSize: '14px' }} />
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="caption" color="#64748b" display="block" noWrap>
                        Konu: {tmpl.subject}
                      </Typography>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Sağ Panel: Gönderici Formu */}
          <Box sx={{ width: { xs: '100%', md: '60%' }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="subtitle2" color="#475569" fontWeight={700}>
              E-posta Bilgileri
            </Typography>

            {/* Target email address notice */}
            <Box display="flex" gap={1} bgcolor="#e0f2fe" p={1.5} borderRadius={2} border="1px solid #bae6fd" alignItems="center">
              <Typography variant="body2" color="#0369a1" fontWeight={600}>
                Alıcı Adresi: {formData.email}
              </Typography>
            </Box>

            <TextField
              label="Konu"
              size="small"
              fullWidth
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              sx={textFieldStyle}
              placeholder="E-posta konusunu yazın veya soldan şablon seçin"
              disabled={sendingEmail}
            />

            <TextField
              label="Mesaj İçeriği"
              size="small"
              multiline
              rows={10}
              fullWidth
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Mesaj içeriğini yazın veya soldan şablon seçin..."
              sx={textFieldStyle}
              disabled={sendingEmail}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button
            onClick={() => setOpenEmailModal(false)}
            variant="outlined"
            disabled={sendingEmail}
            sx={{ textTransform: 'none', color: '#64748b', borderColor: '#cbd5e1', borderRadius: 2, fontWeight: 600 }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
            startIcon={sendingEmail ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: '#0284c7',
              '&:hover': { bgcolor: '#0369a1' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3
            }}
          >
            {sendingEmail ? 'Gönderiliyor...' : 'Gönder'}
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  );
};

export default CustomerCard;
